/**
 * uploadToR2 — shared helper for Upload* nodes, the timeline and LMX.
 *
 * Envío AP-2: antes de subir valida contra el plan del usuario y elige la vía.
 *  · Directa (archivos grandes): pide una URL firmada, el navegador sube el
 *    archivo con PUT al almacenamiento y el servidor lo verifica y registra.
 *    No pasa por el servidor, así que no hay tope de 50 MB ni de tiempo.
 *  · Clásica (archivos chicos o sin subida directa): POST /api/files/upload.
 * Los errores salen como UploadError con un mensaje claro (err.userMessage).
 * Keeps payload out of project JSON (no base64).
 */
import axios from 'axios';
import {
  UploadError, chooseRoute, precheck, toUploadError, unavailableMessage, kindOf,
} from './uploadLimits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIMITS_TTL_MS = 60 * 1000;

let limitsCache = null;
let limitsAt = 0;
let limitsPromise = null;
// Si el PUT directo falla por red (p. ej. falta el CORS del bucket), en esta
// sesión se deja de intentar y se usa la vía clásica.
let directBroken = false;

export function _resetUploadStateForTests() {
  limitsCache = null; limitsAt = 0; limitsPromise = null; directBroken = false;
}

/** Límites del usuario actual (cacheados 1 min). null si el servidor no los informa. */
export async function getUploadLimits({ force = false } = {}) {
  if (!force && limitsCache && Date.now() - limitsAt < LIMITS_TTL_MS) return limitsCache;
  if (!limitsPromise) {
    limitsPromise = axios.get(`${API}/files/upload-limits`, { timeout: 15000 })
      .then((res) => {
        limitsCache = res.data?.limits_mb ? res.data : null;
        limitsAt = Date.now();
        return limitsCache;
      })
      .catch(() => null)
      .finally(() => { limitsPromise = null; });
  }
  return limitsPromise;
}

export function peekUploadLimits() {
  return limitsCache;
}

function putFile(url, file, headers, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new UploadError('failed', 'El almacenamiento rechazó el archivo. Probá de nuevo.', { status: xhr.status }));
    };
    xhr.onerror = () => reject(new UploadError('direct_network', 'Se cortó la conexión durante la subida.', { status: 0 }));
    xhr.onabort = () => reject(new UploadError('aborted', 'La subida se canceló.'));
    xhr.send(file);
  });
}

async function directUpload(file, { onProgress, role, origin }) {
  const type = file.type || 'application/octet-stream';
  const { data } = await axios.post(`${API}/files/upload-url`, {
    filename: file.name || 'archivo', content_type: type, size: file.size,
  }, { timeout: 20000 });
  await putFile(data.upload_url, file, data.headers || { 'Content-Type': type }, onProgress);
  const done = await axios.post(`${API}/files/upload-complete`, {
    r2_key: data.r2_key, original_name: file.name || 'archivo',
    role: role || 'input', origin: origin || 'ui',
  }, { timeout: 30000 });
  return done.data;
}

async function multipartUpload(file, { onProgress, role, origin }) {
  const form = new FormData();
  form.append('file', file, file.name);
  // Clasificación opcional del asset (ej. Capturar Frame → output/capture_frame).
  // Sin estos campos el endpoint registra role=input, origin=ui como siempre.
  if (role) form.append('role', role);
  if (origin) form.append('origin', origin);
  // 2 min mínimo; con conexiones lentas, ~200 KB/s, hasta 15 min.
  const timeout = Math.min(15 * 60 * 1000, Math.max(120000, (file.size / (200 * 1024)) * 1000));
  const res = await axios.post(`${API}/files/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
    timeout,
  });
  return res.data;
}

function shape(data) {
  if (!data?.url) throw new UploadError('failed', 'No se pudo subir el archivo. Probá de nuevo.');
  return {
    url: data.url,
    r2Key: data.r2_key,
    filename: data.filename,
    originalName: data.original_name,
    size: data.size,
    // Envío LMX-3 · el endpoint ya registra el asset: LMX lo referencia por id.
    assetId: data.asset_id || null,
  };
}

// La vía directa no está (servidor sin el endpoint o desactivada): se puede
// seguir por la clásica.
const DIRECT_SKIP_STATUS = new Set([404, 405, 409, 503]);

export async function uploadToR2(file, { onProgress, role, origin } = {}) {
  if (!file) throw new Error('No file provided');
  if (!kindOf(file)) throw precheck(file, null);

  const limits = await getUploadLimits();
  const early = precheck(file, limits);
  if (early) throw early;

  const opts = { onProgress, role, origin };
  const route = chooseRoute(file, limits, { directBroken });
  if (route === 'unavailable') throw new UploadError('direct_unavailable', unavailableMessage(file, limits));

  if (route === 'direct') {
    try {
      return shape(await directUpload(file, opts));
    } catch (err) {
      const status = err?.response?.status ?? err?.status ?? null;
      const network = err?.code === 'direct_network';
      if (network) directBroken = true;
      if (!(network || DIRECT_SKIP_STATUS.has(status))) throw toUploadError(err);
      if (chooseRoute(file, limits, { directBroken: true }) !== 'multipart') {
        throw new UploadError('direct_unavailable', unavailableMessage(file, limits), { cause: err });
      }
      onProgress?.(0);
    }
  }

  try {
    return shape(await multipartUpload(file, opts));
  } catch (err) {
    throw toUploadError(err);
  }
}
